import type {EntityLeaderboardRows, OtherLeaderboardRows} from 'shells/leaderboard/types';

import type {AiFeatureType} from 'nutshell-graphql-types';
import type {AiStatFragment, AiUserFragment, AiFeatureFragment} from 'nutshell-graphql-types';

import {AI_FEATURES_CONFIG_MAP} from '../features/features-list';

export function getAiUsageValue(stat: AiStatFragment): string {
    switch (stat.__typename) {
        case 'AiPercentageStat':
            return String(stat.percentage);
        case 'AiValueStat':
            return stat.value;
        case 'AiUserStat':
            return stat.user ? stat.user.name : 'N/A';
        case 'AiLimitStat': {
            const limitStat = stat as unknown as {limitValue: number};
            return String(limitStat.limitValue ?? 0);
        }
        default:
            return '';
    }
}

export function getAiUsageType(stat: AiStatFragment): 'text' | 'percent' | 'limit' {
    switch (stat.__typename) {
        case 'AiPercentageStat':
            return 'percent';
        case 'AiLimitStat':
            return 'limit';
        default:
            return 'text';
    }
}

export function getAiUsageLimit(stat: AiStatFragment): number | null | undefined {
    switch (stat.__typename) {
        case 'AiLimitStat':
            return stat.limit;
        default:
            return undefined;
    }
}

export function getAiUsageIsUnlimited(stat: AiStatFragment): boolean | undefined {
    switch (stat.__typename) {
        case 'AiLimitStat':
            return stat.isUnlimited;
        default:
            return undefined;
    }
}

/**
 * Get the number of columns to display based on the number of stats. If a
 * feature is not user based, it will have 2 stats to display, otherwise it will
 * have 4 stats to display.
 *
 * @param {number} aiFeatureType - The type of the feature
 * @returns {number} The number of columns to display
 */
export function getNumberOfStatColumns(aiFeatureType: AiFeatureType): number {
    switch (aiFeatureType) {
        case 'LEAD_SUMMARIZER':
        case 'SALES_CHATBOT':
            return 2;
        default:
            return 4;
    }
}

/**
 * Maps the top AI users from query to leaderboard row data that can be injested
 * by Leaderboard component
 *
 * @param {?Array<AiUserFragment>} topAiUsers - The top AI users
 * @param {boolean} showOutcomeCount - Whether to show the outcome count as secondary value
 * @returns {EntityLeaderboardRows} The leaderboard rows
 */
export function mapTopAiUsersToLeaderboardRows(
    topAiUsers?: AiUserFragment[] | null,
    showOutcomeCount?: boolean
): EntityLeaderboardRows {
    if (!topAiUsers || topAiUsers.length === 0) {
        return [];
    }

    return topAiUsers.map((user) => ({
        id: user.user.id,
        rowType: 'entity',
        entityType: 'users',
        initials: user.user.initials,
        avatarUrl: user.user.avatarUrl,
        htmlUrl: null,
        label: user.user.name,
        value: user.timeSaved,
        secondaryValue: showOutcomeCount
            ? `${user.outcomeCount} outcome${user.outcomeCount === 1 ? '' : 's'}`
            : undefined,
    }));
}

/**
 * Maps the top AI features from query to leaderboard row data that can be injested
 * by Leaderboard component
 *
 * @param {?Array<AiFeatureFragment>} topAiFeatures - The top AI features
 * @returns {OtherLeaderboardRows} The leaderboard rows
 */

export function mapTopAiFeaturesToLeaderboardRows(
    topAiFeatures?: AiFeatureFragment[] | null,
    selectedPeriod?: string
): OtherLeaderboardRows {
    if (!topAiFeatures || topAiFeatures.length === 0) {
        return [];
    }

    const periodQuery = selectedPeriod ? `?period=${selectedPeriod.toLowerCase()}` : '';

    // @ts-expect-error
    return topAiFeatures
        .filter((feature) => {
            // Filter out features that aren't in the config map (e.g., filtered
            // out by feature flags)
            return AI_FEATURES_CONFIG_MAP.has(feature.type);
        })
        .map((feature) => {
            const stats = feature.__typename === 'AiFeatureWithStats' ? feature.stats : [];

            const overallTimeSavedStat = stats.find(
                (stat) => stat.displayName === 'Overall time saved'
            );

            const featureConfig = AI_FEATURES_CONFIG_MAP.get(feature.type);
            const pathSegment = featureConfig
                ? `/ai/home/${featureConfig.pathSegment}${periodQuery}`
                : undefined;
            const featureIcon = featureConfig ? featureConfig.icon : null;

            return {
                id: feature.type,
                rowType: 'other',
                icon: featureIcon,
                iconColor: 'aiPurplePrimary',
                label: feature.displayName,
                pathName: pathSegment,
                value:
                    overallTimeSavedStat && overallTimeSavedStat.__typename === 'AiValueStat'
                        ? overallTimeSavedStat.value
                        : '0m',
            };
        });
}
